import { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
// @ts-ignore - leaflet.heat has no TypeScript types
import 'leaflet.heat';
import { MapPin, Users, FileText, AlertTriangle, Eye } from 'lucide-react';
import { HeatmapData, HotspotData } from '../types';
import 'leaflet/dist/leaflet.css';

interface SpatialBiasHeatmapProps {
    heatmapData: HeatmapData | null;
    hotspots: HotspotData[];
    apiKey?: string;
    defaultCenter?: { lat: number; lng: number };
    isLoading?: boolean;
}

type HeatmapMode = 'reports' | 'reporters' | 'bias';

// Heat layer using leaflet.heat (loaded dynamically to avoid TS issues)
function HeatLayer({ points, gradient }: { points: [number, number, number][]; gradient?: Record<string, string> }) {
    const map = useMap();
    useEffect(() => {
        if (!points.length) return;
        // @ts-ignore - leaflet.heat adds L.heatLayer
        const heat = (L as any).heatLayer(points, {
            radius: 30,
            blur: 20,
            maxZoom: 17,
            max: 1.0,
            gradient: gradient || { 0.0: 'blue', 0.4: 'cyan', 0.65: 'lime', 0.8: 'yellow', 1.0: 'red' },
        });
        heat.addTo(map);
        return () => { map.removeLayer(heat); };
    }, [points, gradient, map]);
    return null;
}

function FitBounds({ center }: { center?: { lat: number; lng: number } }) {
    const map = useMap();
    const movedRef = useRef(false);
    useEffect(() => {
        if (center && !movedRef.current) {
            movedRef.current = true;
            map.setView([center.lat, center.lng], 13);
        }
    }, [center, map]);
    return null;
}

export default function SpatialBiasHeatmap({
    heatmapData,
    hotspots,
    defaultCenter,
    isLoading: externalLoading,
}: SpatialBiasHeatmapProps) {
    const [mode, setMode] = useState<HeatmapMode>('reports');
    const [showHotspotOverlay, setShowHotspotOverlay] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState<string>('all');

    const allReportPoints = heatmapData?.report_points || [];
    const filteredReportPoints = selectedCategory === 'all'
        ? allReportPoints
        : allReportPoints.filter(p => p.category === selectedCategory);

    // Use weight=1.0 for each individual report point so leaflet.heat renders
    // them at full intensity. With many overlapping points the radius/blur
    // creates natural density clusters.
    const reportPoints: [number, number, number][] = filteredReportPoints
        .map(p => [p.lat, p.lng, 1.0] as [number, number, number]);

    const reporterPoints: [number, number, number][] = (heatmapData?.reporter_points || [])
        .map(p => [p.lat, p.lng, 1.0] as [number, number, number]);

    // Bias: report density vs reporter density — highlight over-reported areas
    const biasPoints: [number, number, number][] = filteredReportPoints.map(p => {
        const reporterPt = heatmapData?.reporter_points.find(r =>
            Math.abs(r.lat - p.lat) < 0.001 && Math.abs(r.lng - p.lng) < 0.001
        );
        const bias = reporterPt ? p.weight / Math.max(reporterPt.weight, 1) : p.weight;
        return [p.lat, p.lng, Math.min(bias, 1)] as [number, number, number];
    });

    const activePoints = mode === 'reports' ? reportPoints : mode === 'reporters' ? reporterPoints : biasPoints;
    const biasGradient = { 0.0: '#22c55e', 0.5: '#f59e0b', 1.0: '#ef4444' };

    // If we have actual report points, center on their average so the map
    // always shows the data rather than a hardcoded fallback.
    let computedCenter = defaultCenter;
    if (!computedCenter && reportPoints.length > 0) {
        const avgLat = reportPoints.reduce((s, p) => s + p[0], 0) / reportPoints.length;
        const avgLng = reportPoints.reduce((s, p) => s + p[1], 0) / reportPoints.length;
        computedCenter = { lat: avgLat, lng: avgLng };
    }
    const defaultCenterFallback = computedCenter || { lat: 4.1755, lng: 73.5093 };

    if (externalLoading) {
        return (
            <div className="h-full min-h-[400px] flex items-center justify-center bg-white/5 rounded-2xl border border-white/10">
                <div className="w-10 h-10 border-3 border-indigo-500 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="h-full min-h-[580px] flex flex-col">
            {/* Mode switcher */}
            <div className="flex gap-2 mb-3">
                {([
                    { key: 'reports', label: 'Reports', icon: FileText },
                    { key: 'reporters', label: 'Reporters', icon: Users },
                    { key: 'bias', label: 'Bias', icon: AlertTriangle },
                ] as const).map(({ key, label, icon: Icon }) => (
                    <button
                        key={key}
                        onClick={() => setMode(key)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                            mode === key
                                ? 'bg-indigo-600 text-white'
                                : 'bg-white/10 text-white/60 hover:bg-white/20 hover:text-white/80'
                        }`}
                    >
                        <Icon className="w-3.5 h-3.5" />
                        {label}
                    </button>
                ))}
                <button
                    onClick={() => setShowHotspotOverlay(v => !v)}
                    className={`ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        showHotspotOverlay ? 'bg-white/20 text-white' : 'bg-white/5 text-white/40'
                    }`}
                >
                    <Eye className="w-3.5 h-3.5" />
                    Hotspots
                </button>
            </div>

            {/* Category filter */}
            {heatmapData?.categories && heatmapData.categories.length > 0 && (
                <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs text-white/60 font-medium">Category:</span>
                    <select
                        value={selectedCategory}
                        onChange={e => setSelectedCategory(e.target.value)}
                        className="flex-1 max-w-xs bg-white/10 text-white text-xs rounded-lg px-3 py-1.5 border border-white/10 focus:outline-none focus:border-indigo-400"
                    >
                        <option value="all" className="bg-slate-800">All categories ({allReportPoints.length})</option>
                        {heatmapData.categories.map(cat => {
                            const count = allReportPoints.filter(p => p.category === cat).length;
                            return (
                                <option key={cat} value={cat} className="bg-slate-800">
                                    {cat} ({count})
                                </option>
                            );
                        })}
                    </select>
                    {selectedCategory !== 'all' && (
                        <button
                            onClick={() => setSelectedCategory('all')}
                            className="text-xs text-indigo-300 hover:text-indigo-200 px-2"
                        >
                            Clear
                        </button>
                    )}
                </div>
            )}

            <div className="flex-1 relative rounded-2xl overflow-hidden border border-white/10 shadow-xl">
                <MapContainer
                    center={[defaultCenterFallback.lat, defaultCenterFallback.lng]}
                    zoom={13}
                    className="h-full w-full"
                    style={{ background: '#1a1a2e', minHeight: '520px' }}
                >
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <FitBounds center={defaultCenterFallback} />

                    {activePoints.length > 0 && (
                        <HeatLayer
                            points={activePoints}
                            gradient={mode === 'bias' ? biasGradient : undefined}
                        />
                    )}

                    {showHotspotOverlay && hotspots.map((hs, i) => {
                        const radius = Math.max(12, Math.min(30, 12 + hs.count));
                        return (
                            <CircleMarker
                                key={i}
                                center={[hs.lat, hs.lng]}
                                radius={radius}
                                pathOptions={{
                                    fillColor: hs.count > 20 ? '#ef4444' : hs.count > 10 ? '#f59e0b' : '#6366f1',
                                    fillOpacity: 0.85,
                                    color: 'white',
                                    weight: 1.5,
                                }}
                            >
                                <Popup>
                                    <div className="p-1 min-w-[160px]">
                                        <div className="flex items-center gap-1 mb-1">
                                            <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                                            <span className="font-semibold text-sm">Hotspot #{hs.cluster_id}</span>
                                        </div>
                                        <p className="text-xs text-gray-600">{hs.count} reports</p>
                                        {hs.unique_reporters && (
                                            <p className="text-xs text-gray-500">{hs.unique_reporters} unique reporters</p>
                                        )}
                                        {hs.sample_address && (
                                            <p className="text-xs text-gray-500 mt-1">{hs.sample_address}</p>
                                        )}
                                        {hs.top_categories && hs.top_categories.length > 0 && (
                                            <p className="text-xs text-gray-500 mt-1">
                                                {hs.top_categories.slice(0, 2).join(', ')}
                                            </p>
                                        )}
                                        {hs.oldest_days != null && (
                                            <p className="text-xs text-amber-600 mt-1">Oldest: {hs.oldest_days}d open</p>
                                        )}
                                    </div>
                                </Popup>
                            </CircleMarker>
                        );
                    })}
                </MapContainer>

                {/* Stats overlay */}
                {heatmapData && (
                    <div className="absolute bottom-4 left-4 z-[1000] bg-white/90 backdrop-blur-md rounded-xl border border-gray-200 px-4 py-3 shadow-lg">
                        <div className="flex items-center gap-6 text-xs text-gray-700">
                            <div className="flex items-center gap-1.5">
                                <FileText className="w-3.5 h-3.5 text-indigo-500" />
                                <span><b>{heatmapData.total_reports}</b> reports</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <Users className="w-3.5 h-3.5 text-indigo-500" />
                                <span><b>{heatmapData.total_unique_reporters}</b> reporters</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Mode label */}
                <div className="absolute top-4 left-4 z-[1000] bg-white/90 backdrop-blur-md rounded-xl border border-gray-200 px-3 py-1.5 shadow-lg">
                    <p className="text-xs font-medium text-gray-700">
                        {mode === 'reports' ? 'Report density heatmap'
                            : mode === 'reporters' ? 'Reporter density heatmap'
                                : 'Reporting bias (high = over-reported)'}
                    </p>
                </div>
            </div>
        </div>
    );
}
